import { AxiosResponse } from 'axios';
import { cloudFunctionsAxios, createCloudRunAxios } from './axios';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface TenantBranding {
  colors: {
    primary: string;
    secondary: string;
    accent?: string;
    background?: string;
    text?: string;
  };
  logo: {
    url: string;
    width?: number;
    height?: number;
  };
  favicon?: {
    url?: string;
    width?: number;
    height?: number;
  };
}

export interface TenantDomain {
  primary: string;
  aliases: string[];
}

export interface RegisterDomainRequest {
  organisation_id: string;
  domain: string;
}

export interface RegisterDomainResponse {
  success: boolean;
  organisation_id: string;
  new_domain: string;
  domains: string[];
  results: {
    authentication: {
      success: boolean;
      domain: string;
      updated: boolean;
      message?: string;
    };
    hosting: {
      success: boolean;
      domain: string;
      updated: boolean;
      dns_status: 'configured' | 'needs_setup';
      message?: string;
    };
  };
}

export interface TenantAnalytics {
  // Empty interface since we're not sending any analytics fields in createTenant
}

export interface CreateTenantRequest {
  id: string;
  name: string;
  domain: TenantDomain;
  analytics: TenantAnalytics;
  branding: TenantBranding;
}

export interface UpdateTenantRequest {
  name?: string;
  analytics: TenantAnalytics;
  branding?: TenantBranding;
  skipRentals?: boolean;
}

export interface VerificationStatus {
  tenant: boolean;
  domain: boolean;
  firebase: boolean;
  analytics: boolean;
  errors?: string[];
}

export interface VerificationResponse {
  verification: VerificationStatus;
}

export interface TenantResponse {
  id: string;
  name: string;
  domain: TenantDomain;
  analytics: TenantAnalytics;
  createdAt: string;
  updatedAt: string;
  cities?: string[];
}

export interface CitySubscriptionRequest {
  tenant_id: string;
  city_ids: string[];
}

export const tenantManagementApi = {
  /**
   * Create a new tenant
   * @param data Tenant creation data
   * @returns Promise with the created tenant data
   */
  createTenant: async (
    data: CreateTenantRequest,
    orgLinks?: { key: string; value: string }[]
  ): Promise<AxiosResponse<TenantResponse>> => {
    const response = await cloudFunctionsAxios.post('/tenantManagement/tenant', data);

    // Update organization in Firestore
    const orgRef = doc(db, 'Organizations', data.id);
    const updateData: Record<string, unknown> = {
      logoUrl: data.branding?.logo?.url || '',
      faviconUrl: data.branding?.favicon?.url || '',
      branding: {
        primaryColor: data.branding?.colors?.primary || '',
        secondaryColor: data.branding?.colors?.secondary || '',
      },
    };

    // Add org_links if provided
    if (orgLinks?.length) {
      const links = orgLinks.reduce(
        (acc, link) => {
          acc[link.key] = link.value;
          return acc;
        },
        {} as Record<string, string>
      );
      updateData.org_links = links;
    }

    try {
      await updateDoc(orgRef, updateData);
    } catch (error) {
      // Don't block tenant creation if client-side org doc patch is denied.
      // In UAT, non-super-admin users can create tenants via Cloud Function,
      // but may not have Firestore write permission on Organizations.
      console.warn('Tenant created but failed to patch Organizations document:', error);
    }

    return response;
  },

  /**
   * Update an existing tenant
   * @param tenantId ID of the tenant to update
   * @param data Update data (currently only analytics can be updated)
   * @returns Promise with the updated tenant data
   */
  updateTenant: async (
    tenantId: string,
    data: UpdateTenantRequest
  ): Promise<AxiosResponse<TenantResponse>> => {
    return cloudFunctionsAxios.put(`/tenantManagement/tenant/${tenantId}`, data);
  },

  /**
   * Verify a tenant's setup and configuration
   * @param tenantId ID of the tenant to verify
   * @returns Promise with verification status for different aspects of the tenant
   */
  verifyTenant: async (tenantId: string): Promise<AxiosResponse<VerificationResponse>> => {
    return cloudFunctionsAxios.get(`/tenantManagement/tenant/verify/${tenantId}`);
  },

  /**
   * Delete a tenant
   * @param tenantId ID of the tenant to delete
   * @returns Promise with the deletion confirmation
   */
  deleteTenant: async (tenantId: string): Promise<AxiosResponse<{ success: boolean }>> => {
    return cloudFunctionsAxios.delete(`/tenantManagement/tenant/${tenantId}`);
  },

  /**
   * Setup analytics configuration for a tenant
   * @param data Analytics setup data including organization ID and script tags
   * @returns Promise with the setup confirmation
   */
  /**
   * Subscribe tenant to cities
   * @param data City subscription data including tenant ID and city IDs
   * @returns Promise with the subscription confirmation
   */
  subscribeToCity: async (
    data: CitySubscriptionRequest
  ): Promise<AxiosResponse<{ success: boolean }>> => {
    return cloudFunctionsAxios.post('/city-subscription', data);
  },

  analyticsSetup: async (data: {
    organization_id: string;
    googleTag?: string;
    gtmHead?: string;
    gtmBody?: string;
    gaHead?: string;
  }): Promise<AxiosResponse<{ success: boolean }>> => {
    const analyticsAxios = createCloudRunAxios('analytics-setup');
    return analyticsAxios.post('/', data);
  },

  /**
   * Register a domain in Firebase Authentication and Firebase Hosting.
   * This goes through the tenantManagement Cloud Function proxy so browser callers
   * do not hit the Cloud Run service directly and trip CORS/network errors.
   * @param data Domain registration data including client_name and domain
   * @returns Promise with the registration result
   */
  registerDomain: async (
    data: RegisterDomainRequest
  ): Promise<AxiosResponse<RegisterDomainResponse>> => {
    return cloudFunctionsAxios.post('/tenantManagement/register-domain', data);
  },
};
